// ABOUTME: Unassigned tickets view: selectable grid on the left, stuck/unassigned card list on the right.
// ABOUTME: Bulk-assign via action modal; sidebar card click opens the ticket in a bottom modal.

import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { formatDistanceToNowStrict } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";

import DropdownButton from '../../../../../components/theme/buttons/DropdownButton';
import ShDataGrid2 from "../../../../../components/theme/datagrid2/ShDataGrid2";
import ShDataGridSelectedActions from '../../../../../components/theme/datagrid2/ShDataGridSelectedActions';
import ShTextSkeleton from "../../../../../components/theme/skeleton/ShTextSkeleton";
import Heading from "../../../../../components/theme/text/Heading";
import Subheading from "../../../../../components/theme/text/Subheading";
import Text from "../../../../../components/theme/text/Text";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../components/theme/TabView3/IconColorGenerator";
import { ReactComponent as TableEmpty } from "../../../../../styles/svg/tableempty.svg";

import { selectUserPermission } from "../../../../../newapi/user/authentication/authSlice";
import {
    useGetDashboardStaleListQuery,
    useGetDashboardUnassignedListQuery,
} from "../../../../../newapi/warehouse/supportTicketApiSlice";

import { ServicedeskTicketCardWarehouse } from "../components/ServicedeskTicketCard";
import MBulkAssignSupportTickets from "../components/modals/MBulkAssignSupportTickets";
import MTicketDetailBottom from "../components/modals/MTicketDetailBottom";
import { tokens } from "../../../../../theme";
import { localeSettings } from "../../../../../utils/locales";
import { UserPrivileges } from '../../../../../utils/permissions';
import PriorityBadge from "../components/PriorityBadge";

const gridOptions = {
    export: true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const STALE_FILTER = {
    UNASSIGNED: 'unassigned',
    STUCK: 'stuck',
};

const TypeBadge = ({ type }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (!type) return null;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: generateColorFromTitle(type, theme.palette.mode, 90),
                borderRadius: 5,
                px: 1.5,
                py: 0.5,
                height: 24,
                lineHeight: '24px',
            }}
        >
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: generateIconColorFromTitle(type, theme.palette.mode),
                    flexShrink: 0,
                }}
            />
            <Text bold sx={{ fontSize: 13 }}>{t(type)}</Text>
        </Box>
    );
};

const StaleList = ({ filter, onTicketClick }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { data, isLoading } = useGetDashboardStaleListQuery(
        { filter },
        { pollingInterval: 60000 }
    );

    const tickets = data?.data ?? [];
    const isEmpty = !isLoading && tickets.length === 0;

    return (
        <>
            {isLoading && [1, 2, 3, 4].map((i) => (
                <Box
                    key={`skeleton-${i}`}
                    sx={{
                        px: 4,
                        py: 3,
                        borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <ShTextSkeleton />
                </Box>
            ))}

            {isEmpty && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Box
                        sx={{
                            maxWidth: 300,
                            display: 'flex',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <TableEmpty />
                        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Heading align="center">{t('no_stuck_tickets')}</Heading>
                            <Text align="center" light>{t('grid_empty_description')}</Text>
                        </Box>
                    </Box>
                </Box>
            )}

            {!isLoading && tickets.map((ticket) => {
                const timestamp = ticket.lastActivityAt || ticket.createdAt;
                return (
                    <ServicedeskTicketCardWarehouse
                        key={ticket.id}
                        ticket={{
                            id: ticket.id,
                            ticketNumber: ticket.ticketNumber,
                            title: ticket.title,
                            type: ticket.type,
                            priority: ticket.priority?.toLowerCase(),
                            lastUpdateTime: timestamp
                                ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                : '',
                            relationName: ticket.relationName || 'Unknown',
                            contactName: ticket.contactName || '',
                            updates: [],
                        }}
                        sx={{
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                            borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100]}`,
                        }}
                        onClick={() => onTicketClick(ticket)}
                    />
                );
            })}
        </>
    );
};

const UnassignedTickets = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Body for the dashboard/unassigned-list endpoint. relationId is optional;
    // the marker key keeps ShDataGrid2 from skipping (it bails on an empty body).
    const [config, setConfig] = useState({
        ...(props.tableConfig ?? {}),
        relationId: null,
    });

    useEffect(() => {
        if (props.tableConfig) {
            setConfig((prev) => ({ ...prev, ...props.tableConfig }));
        }
    }, [props.tableConfig]);

    const hasManagePermission = useSelector((state) =>
        selectUserPermission(state, [UserPrivileges.SUPPORT_MANAGE])
    );

    const [selectedItems, setSelectedItems] = useState([]);
    const onSelected = (locSelectedItems) => setSelectedItems(locSelectedItems);

    const [assignModalOpen, setAssignModalOpen] = useState(false);

    const [staleFilter, setStaleFilter] = useState(STALE_FILTER.UNASSIGNED);

    const [detailTicketId, setDetailTicketId] = useState(null);
    const detailOpen = detailTicketId !== null;

    const openTicketDetail = (ticket) => setDetailTicketId(ticket.id);
    const closeTicketDetail = () => setDetailTicketId(null);

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
        });
    }, []);

    const getTicketDetails = (params) => {
        if (!params?.row?.id) return;
        setDetailTicketId(params.row.id);
    };

    const columns = useMemo(() => [
        {
            field: "title",
            headerName: t('name'),
            flex: 2,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;

                return (
                    <Box
                        sx={{
                            display:"flex",
                            alignItems:"center",
                            gap:1.5,
                        }}
                    >
                        <PriorityBadge
                            priority={params.row.priority}
                        />
                        <Box
                            sx={{
                                display:"flex",
                                flexDirection:"column",
                            }}
                        >
                            <Text noBreak>{params.row.title}</Text>
                            <Text light bold>{params.row.ticketNumber}</Text>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "relation",
            headerName: t('created_by'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Text>{params.row.relation?.name || '—'}</Text>
                        <Text light sx={{ fontSize: 12 }}>
                            {params.row.relation?.contactName || ''}
                        </Text>
                    </Box>
                );
            },
        },
        {
            field: "type",
            headerName: t('type'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <TypeBadge type={params.row.type} />;
            },
        },
        {
            field: "createdAt",
            headerName: t('received'),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                const locale = localeSettings[i18n.language]?.dateLocale;
                const label = formatDistanceToNowStrict(new Date(params.row.createdAt), {
                    addSuffix: true,
                    locale,
                });
                return <Text light>{label}</Text>;
            },
        },
        {
            field: "phoneView",
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: 1, py: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text bold>#{params.row.ticketNumber}</Text>
                            <TypeBadge type={params.row.type} />
                        </Box>
                        <Text noBreak>{params.row.title}</Text>
                        <Text light sx={{ fontSize: 12 }}>
                            {params.row.relation?.name || '—'}
                        </Text>
                    </Box>
                );
            },
        },
    ], [t, i18n.language, theme.palette.mode]);

    const defaultSort = useMemo(
        () => ({ field: 'createdAt', type: 'ASC' }),
        []
    );

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "1fr 420px",
                minHeight: 1,
                "@media screen and (max-width: 63.9375em)": {
                    gridTemplateColumns: "1fr",
                },
            }}
        >
            {/* Left - selectable grid */}
            <Box
                sx={{
                    borderRight: `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                }}
            >
                <ShDataGrid2
                    key={theme.palette.mode}
                    pollingInterval={true}
                    content={useGetDashboardUnassignedListQuery}
                    config={config}
                    title={t('new_incoming')}
                    gridOptions={gridOptions}
                    sortModel={defaultSort}
                    columns={columns}
                    onRowClick={getTicketDetails}
                    selectable={hasManagePermission}
                    setSelected={onSelected}
                    keepSelectedAlive
                    keepNonExistentRowsSelected
                    selectedItems={selectedItems}
                    sideCarRight={420}
                    selectedActions={
                        <ShDataGridSelectedActions selectedItems={selectedItems}>
                            {hasManagePermission && (
                                <DropdownButton
                                    onClick={() => setAssignModalOpen(true)}
                                    icon={<AssignmentIndIcon />}
                                    text={t('assign_to_user')}
                                />
                            )}
                        </ShDataGridSelectedActions>
                    }
                />
            </Box>

            {/* Right - stale list with toggle */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                    display: 'flex',
                    flexDirection: 'column',
                    "@media screen and (max-width: 63.9375em)": {
                        display: 'none',
                    },
                }}
            >
                <Box
                    sx={{
                        p:3,
                        display: 'flex',
                        // Title above the filter pills: side by side they fight
                        // for the fixed rail width and the title gets cropped.
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1.5,
                    }}
                >

                    <Subheading>
                        {t("seven_days")}
                    </Subheading>

                    <ToggleButtonGroup
                        value={staleFilter}
                        exclusive
                        onChange={(_, v) => v && setStaleFilter(v)}
                        sx={{
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                            borderRadius: 5,
                            p: 0.5,
                            gap: 0.5,
                            height: 32,
                            "& .MuiToggleButtonGroup-grouped": {
                                border: 0,
                                borderRadius: "12px !important",
                                whiteSpace:"nowrap",
                                flex: 1,
                                textTransform: 'none',
                                borderRadius: 5,
                                fontSize: 14,
                                fontFamily: "Roboto, sans-serif",
                                fontWeight: 400,
                                color: `${colors.txt['secondary']} !important`,
                                py: 1,
                                color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                "&.Mui-selected": {
                                    color: colors.txt['primary'],
                                    fontWeight: 600,
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? "none" : `1px solid ${colors.grey[100]}`,
                                    boxShadow: theme.palette.mode === "dark" ? "none" : `0px 2px 4px ${colors.grey[200]}`,
                                    color: theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt["primary"],
                                },
                                "&:hover": {
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                                },
                            },
                        }}
                    >
                        <ToggleButton value={STALE_FILTER.UNASSIGNED}>
                            {t('unassigned')}
                        </ToggleButton>
                        <ToggleButton value={STALE_FILTER.STUCK}>
                            {t('no_activity')}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* <Subheading>
                        {staleFilter === STALE_FILTER.UNASSIGNED
                            ? t('stuck_7_days_not_assigned')
                            : t('no_activity_7_days')}
                    </Subheading> */}
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <StaleList filter={staleFilter} onTicketClick={openTicketDetail} />
                </Box>
            </Box>

            <MBulkAssignSupportTickets
                open={assignModalOpen}
                handleClose={() => setAssignModalOpen(false)}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
            />

            <MTicketDetailBottom
                open={detailOpen}
                onClose={closeTicketDetail}
                ticketId={detailTicketId}
            />
        </Box>
    );
};

export default UnassignedTickets;
